/**
 * Konfidenz-Berechnung pro Dimension.
 *
 * Konzept §5.5: „Pro Dimension werden Pflicht-Felder definiert. Die
 * Konfidenz ergibt sich aus dem Anteil ausgefüllter Pflicht-Felder,
 * gewichtet nach Dimension. Liegt die Konfidenz unter 60 Prozent, wird
 * die Stellungnahme als nicht abschließend markiert."
 *
 * Pflichtfelder pro Dimension wurden hier so definiert, dass sie
 * minimale, fachlich notwendige Informationen abbilden. Die Liste ist
 * additiv erweiterbar — eine spätere Verschärfung verschiebt nur die
 * Konfidenz, bricht keine API.
 *
 * Gibt zurück: 0-100 (Prozent ausgefüllter Pflichtfelder).
 */
import type { Percentage } from '../schema/brands.js';
import type { DimensionPayload, DimensionKey } from '../schema/dimensionData.js';
import { DIMENSION_WEIGHTS } from './computeTotalScore.js';
import type { FinancialDataset } from '../schema/financialDataset.js';

type FieldCheck = boolean;

function pct(filled: number, total: number): Percentage {
  if (total === 0) return 100 as Percentage;
  return Math.round((filled / total) * 100) as Percentage;
}

function nonEmpty(s: string | undefined, minLength = 1): FieldCheck {
  return typeof s === 'string' && s.trim().length >= minLength;
}

export function computeDimensionConfidence(
  payload: DimensionPayload,
  financialDataset?: FinancialDataset,
): Percentage {
  switch (payload.dimension) {
    case 'identity': {
      const checks: FieldCheck[] = [
        payload.qualifications.length > 0,
        typeof payload.industryExperienceYears === 'number',
        true, // hasEntrepreneurialExperience ist boolean → immer „beantwortet"
        nonEmpty(payload.usp, 20),
        payload.personalTraits.length >= 2,
      ];
      return pct(checks.filter(Boolean).length, checks.length);
    }
    case 'activity': {
      const checks: FieldCheck[] = [
        nonEmpty(payload.valueProposition, 20),
        nonEmpty(payload.targetCustomers, 20),
        payload.topClientsByRevenue.length > 0,
        true, // hasRecurringRevenue boolean
        true, // scalability enum (immer gesetzt)
        true, // hasDocumentedSalesProcess boolean
      ];
      return pct(checks.filter(Boolean).length, checks.length);
    }
    case 'style': {
      const checks: FieldCheck[] = [
        true, // targetGroupDefined boolean
        nonEmpty(payload.targetGroupDescription, 20),
        payload.competitors.length > 0,
        nonEmpty(payload.differentiators, 20),
        typeof payload.selfReportedHourlyRateEuro === 'number',
        true, // pricingStrategyDocumented boolean
      ];
      return pct(checks.filter(Boolean).length, checks.length);
    }
    case 'resources': {
      // Dimension 4 hängt am FinancialDataset — Konfidenz reflektiert dessen
      // Vollständigkeit, nicht die der DimensionData-Notes.
      if (!financialDataset) return 0 as Percentage;
      const checks: FieldCheck[] = [
        financialDataset.monthlyAggregates.length >= 3,
        typeof financialDataset.hourlyRate.billableHoursPerMonth === 'number',
        typeof financialDataset.hourlyRate.hourlyRateNetCents === 'number',
        financialDataset.liquidity !== undefined,
        true, // bgComposition ist Pflichtfeld im FinancialDataset
      ];
      return pct(checks.filter(Boolean).length, checks.length);
    }
    case 'health': {
      const checks: FieldCheck[] = [
        typeof payload.selfReportedResilienceScore === 'number',
        typeof payload.hoursWorkedPerWeek === 'number',
        true, // hasMedicalCertification boolean
        true, // chronicConditionsExist boolean
        true, // conditionsAffectWork boolean
        nonEmpty(payload.copingStrategiesDescribed, 20),
      ];
      return pct(checks.filter(Boolean).length, checks.length);
    }
    case 'private': {
      const checks: FieldCheck[] = [
        true, // childcareSecured boolean
        true, // hasCareResponsibility boolean
        nonEmpty(payload.zumutbarkeitsgruende, 30),
        nonEmpty(payload.regionalLaborMarketContext, 20),
        // BG-Komposition wird über financialDataset abgedeckt
        financialDataset !== undefined,
      ];
      return pct(checks.filter(Boolean).length, checks.length);
    }
  }
}

/**
 * Gewichteter Durchschnitt der Dimensions-Konfidenzen.
 *
 * Berücksichtigt nur die in `perDimension` enthaltenen (touched) Dimensionen
 * und normalisiert über deren Gewichtssumme — analog zum Total-Score. Damit
 * spiegelt die Gesamtkonfidenz die Datengrundlage der bisher bearbeiteten
 * Dimensionen wider, ohne dass leere Dimensionen den Wert nach unten ziehen.
 *
 * Für die `state`-Berechnung in `computeScoreResult` wird zusätzlich geprüft,
 * ob alle 6 Dimensionen erfasst sind. Eine hohe Gesamtkonfidenz auf nur einer
 * Dimension reicht NICHT für `state='final'`.
 *
 * Bei leerem Input → 0%.
 */
export function computeTotalConfidence(
  perDimension: Readonly<Partial<Record<DimensionKey, Percentage>>>,
): Percentage {
  let sum = 0;
  let weightSum = 0;
  for (const dim of Object.keys(DIMENSION_WEIGHTS) as DimensionKey[]) {
    const c = perDimension[dim];
    if (c === undefined) continue;
    const w = DIMENSION_WEIGHTS[dim];
    sum += (c as number) * w;
    weightSum += w;
  }
  if (weightSum === 0) return 0 as Percentage;
  return Math.round(sum / weightSum) as Percentage;
}

/** Schwellenwert nach Konzept §5.5 — unter diesem Wert: nicht abschließend. */
export const CONFIDENCE_FINAL_THRESHOLD: Percentage = 60 as Percentage;
